/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Dmitry Pavlov
 * @version 1.23.0
 * @since 1.0.0
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent, ReactElement, ReactNode } from 'react';

import Tooltip from '@material-ui/core/Tooltip';

import { DEBOUNCE_DEFAULT_DELAY } from '@/main/modules/app/appConstants';
import type {
  TOverflow,
  TTextAlign,
  TTooltipPlacement,
} from '@/main/types/globalTypes';

import LinkBlock from '../LinkBlock/LinkBlock';
import {
  TooltipContainerStyled,
  TooltipHintMessageStyled,
  TooltipMessageStyled,
  TooltipTitleStyled,
} from './styled/TooltipStyled';
import { useStylesArrow } from './utils/TooltipUtils';

/**
 * @description Define tooltip props.
 * @interface IArrowTooltipProps
 *
 * @property {ReactElement} children Elements, which hovering leads to showing tooltip.
 * @property {string} message Additional text in tooltip
 * @property {'bottom' | 'right' | 'top'} placement Tooltip placement relative to container
 * @property {string} title Tooltip text title
 * @property {string} titleLink Tooltip title link
 */
export interface IArrowTooltipProps {
  children: ReactElement;
  closeTooltipOnDisableChange: boolean;
  dataName?: string;
  disableHoverListener?: boolean;
  isHintMessage?: boolean;
  maxHeight?: string;
  maxWidth?: string;
  message?: ReactNode;
  placement?: TTooltipPlacement;
  title?: string | number;
  titleLink?: string;
  titleAlign?: TTextAlign;
  overflow?: TOverflow;
  onClick?: (e: MouseEvent) => void;
  onMouseEnter?: (e: MouseEvent) => void;
  onOpen?: () => void;
  onTitleLinkClick?: () => void;
}

/**
 * @description Tooltip showing on element hover. Receives child and shows tooltip when child is hovered.
 * @param {IArrowTooltipProps} props
 * @constructor
 */
function ArrowTooltip(props: IArrowTooltipProps) {
  const { arrow, ...classes } = useStylesArrow()();
  const [arrowRef] = useState(null);
  const disableHoverListener =
    props.disableHoverListener || (!props.title && !props.message);

  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const interactive = useRef(true);

  useEffect(() => {
    if (tooltipIsOpen && props.closeTooltipOnDisableChange) {
      setTooltipIsOpen(!disableHoverListener);
    }
  }, [props.closeTooltipOnDisableChange, tooltipIsOpen, disableHoverListener]);

  const onTooltipOpen = useCallback(() => {
    props.onOpen?.();
    setTooltipIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onOpen]);

  const isTitleWithLink = useMemo(() => {
    const linkRegexp = new RegExp(/<a.*href\s*?=/gi);
    return typeof props.title === 'string' && props.title.match(linkRegexp);
  }, [props.title]);

  const handleOnTitleLinkClick = () => {
    setTooltipIsOpen(false);
    interactive.current = false;
    if (props.onTitleLinkClick) {
      props.onTitleLinkClick();
      setTimeout(() => {
        interactive.current = true;
      }, DEBOUNCE_DEFAULT_DELAY);
    }
  };

  return (
    <Tooltip
      classes={classes}
      data-tool-tip={props.dataName}
      disableHoverListener={disableHoverListener}
      enterDelay={300}
      interactive={interactive.current}
      leaveDelay={100}
      open={tooltipIsOpen}
      placement={props.placement}
      PopperProps={{
        popperOptions: {
          modifiers: {
            arrow: {
              enabled: Boolean(arrowRef),
              element: arrowRef,
            },
          },
        },
      }}
      title={
        <>
          <TooltipContainerStyled
            maxHeight={props.maxHeight}
            maxWidth={props.maxWidth || '280px'}
            overflow={props.overflow || 'hidden'}
            onClick={props.onClick}
          >
            {!!props.title &&
              (!isTitleWithLink ? (
                <TooltipTitleStyled textAlign={props.titleAlign}>
                  {props.title}
                  {props.titleLink && (
                    <LinkBlock
                      className="inquiry-link__button"
                      onClick={handleOnTitleLinkClick}
                    >
                      <span>{props.titleLink}</span>
                    </LinkBlock>
                  )}
                </TooltipTitleStyled>
              ) : (
                <TooltipTitleStyled
                  dangerouslySetInnerHTML={{ __html: String(props.title) }}
                  textAlign={props.titleAlign}
                />
              ))}
            {props.message && props.isHintMessage && (
              <TooltipHintMessageStyled>
                {props.message}
              </TooltipHintMessageStyled>
            )}
            {props.message && !props.isHintMessage && (
              <TooltipMessageStyled>{props.message}</TooltipMessageStyled>
            )}
          </TooltipContainerStyled>
          <span className={arrow} />
        </>
      }
      onClose={() => setTooltipIsOpen(false)}
      onMouseEnter={props.onMouseEnter}
      onOpen={() => onTooltipOpen()}
    >
      {props.children}
    </Tooltip>
  );
}

ArrowTooltip.defaultProps = {
  closeTooltipOnDisableChange: true,
};

export default ArrowTooltip;
